// V9.17 · Página pública do Pedido à Família — responsável completa CPF/endereço no celular.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, ShieldCheck, Camera, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL = {
  cpf: 'CPF do aluno', zip_code: 'CEP', address: 'Endereço (rua/avenida)',
  address_number: 'Número', complement: 'Complemento', neighborhood: 'Bairro',
  city: 'Cidade', state: 'Estado (UF)', email: 'E-mail', phone: 'Telefone',
  cellphone: 'Celular / WhatsApp', birth_date: 'Data de nascimento',
  rg: 'RG', rg_orgao: 'Órgão emissor', rg_data_exp: 'Data de emissão do RG',
};
const TYPE = { birth_date: 'date', rg_data_exp: 'date', email: 'email' };

export default function PedidoCadastroFamilia() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [photoOk, setPhotoOk] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/family-requests/public/${token}`);
        setData(data);
        setValues(data.current || {});
        setDone(data.status === 'submitted');
      } catch (err) {
        setData({ status: 'invalid' });
      } finally { setLoading(false); }
    })();
  }, [token]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const buscarCep = async (cep) => {
    const d = (cep || '').replace(/\D/g, '');
    if (d.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
      const j = await r.json();
      if (j.erro) return toast.error('CEP não encontrado.');
      setValues((s) => ({ ...s, address: j.logradouro || s.address, neighborhood: j.bairro || s.neighborhood,
        city: j.localidade || s.city, state: j.uf || s.state }));
      toast.success('Endereço preenchido pelo CEP');
    } catch (_) { /* silencioso */ }
  };

  const enviar = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/family-requests/public/${token}/submit`, { values });
      setDone(true);
      toast.success('Dados enviados. Obrigado!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível enviar agora.');
    } finally { setSaving(false); }
  };

  const enviarFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !data?.student_id_for_photo) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/students/${data.student_id_for_photo}/photo/public/${token}`, fd);
      setPhotoOk(true);
      toast.success('Foto enviada!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao enviar a foto.');
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-violet-600" /></div>;

  if (!data || data.status === 'invalid' || data.status === 'expired') {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-sm text-center bg-white rounded-2xl border p-8" data-testid="pedido-invalido">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h1 className="text-lg font-bold">Link indisponível</h1>
          <p className="text-sm text-slate-500 mt-2">
            {data?.status === 'expired' ? 'Este link expirou. Peça um novo à secretaria.' : 'Este link não é válido.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white p-5" data-testid="pedido-cadastro-page">
      <div className="max-w-md mx-auto space-y-5">
        <div className="text-center pt-4">
          <ShieldCheck className="w-9 h-9 mx-auto text-violet-600" />
          <h1 className="text-2xl font-bold mt-2">Complete o cadastro</h1>
          <p className="text-sm text-slate-500 mt-1">
            Aluno(a) <b>{data.student_name}</b>{data.class_name ? ` · ${data.class_name}` : ''}
          </p>
          <p className="text-xs text-slate-400 mt-1">{(data.group_labels || []).join(' · ')}</p>
        </div>

        {done ? (
          <div className="bg-white rounded-2xl border p-8 text-center" data-testid="pedido-concluido">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
            <p className="font-bold text-emerald-800">Recebemos seus dados!</p>
            <p className="text-sm text-slate-500 mt-1">A secretaria já pode seguir com a matrícula.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border p-5 space-y-3">
            {(data.fields || []).map((f) => (
              <div key={f}>
                <Label className="text-xs font-semibold text-slate-700">{LABEL[f] || f}</Label>
                <Input
                  type={TYPE[f] || 'text'}
                  value={values[f] || ''}
                  onChange={(e) => set(f, f === 'state' ? e.target.value.toUpperCase() : e.target.value)}
                  onBlur={f === 'zip_code' ? (e) => buscarCep(e.target.value) : undefined}
                  placeholder={f === 'cpf' ? '000.000.000-00' : f === 'zip_code' ? '00000-000' : ''}
                  data-testid={`pedido-campo-${f}`}
                />
              </div>
            ))}
            {data.note && <p className="text-[11px] text-slate-500 italic">{data.note}</p>}
            <Button onClick={enviar} disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700"
              data-testid="pedido-enviar">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Enviar dados
            </Button>
            <p className="text-[10.5px] text-slate-400 text-center">
              Seus dados vão direto para o cadastro do aluno, com segurança.
            </p>
          </div>
        )}

        {data.student_id_for_photo && (
          <div className="bg-white rounded-2xl border p-5 text-center">
            <p className="text-sm font-semibold text-slate-700">Foto do aluno (opcional)</p>
            <p className="text-xs text-slate-500 mt-1">Tire uma foto agora ou escolha da galeria</p>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={enviarFoto} data-testid="pedido-foto-input" />
            <Button variant="outline" className="mt-3" onClick={() => fileRef.current?.click()}
              data-testid="pedido-foto-btn">
              <Camera className="w-4 h-4 mr-1" /> {photoOk ? 'Enviar outra foto' : 'Enviar foto'}
            </Button>
            {photoOk && <p className="text-[11px] text-emerald-700 mt-2">Foto recebida ✓</p>}
          </div>
        )}
      </div>
    </div>
  );
}
